import { ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface PageHeaderProps {
  /**
   * 主標題文字。
   */
  title: string
  /**
   * 麵包屑前綴（顯示為 muted 小字 + '/'，例如 "工具箱"）。
   */
  breadcrumb?: string
  /**
   * 副標題 / 說明文字。
   */
  description?: string
  /**
   * 標題前面的 icon（例如 <Wrench className="h-8 w-8" />）。
   */
  icon?: ReactNode
  /**
   * `'page'`（預設）= 頁面最上層標題，h1 text-3xl。
   * `'section'` = 某個 scope 底下的子頁（例如 /clinic/:id/users），h2 text-2xl。
   *   因為側邊 Logo 已告知目前 scope，子頁標題不必再放大。
   */
  level?: 'page' | 'section'
  /**
   * 右側放的 action（按鈕、搜尋框等）。
   */
  children?: ReactNode
  className?: string
}

export function PageHeader({
  title,
  breadcrumb,
  description,
  icon,
  level = 'page',
  children,
  className,
}: PageHeaderProps) {
  const isPage = level === 'page'
  const Heading = isPage ? 'h1' : 'h2'
  const titleClass = isPage ? 'text-3xl' : 'text-2xl'

  return (
    <div className={cn('flex items-center justify-between', className)}>
      <div>
        <Heading
          className={cn(
            titleClass,
            'font-bold tracking-tight',
            icon && 'flex items-center gap-2',
          )}
        >
          {icon}
          {breadcrumb && (
            <span className="text-muted-foreground font-normal">{breadcrumb} / </span>
          )}
          {title}
        </Heading>
        {description && <p className="text-muted-foreground mt-2">{description}</p>}
      </div>
      {children && <div className="flex gap-2 items-center">{children}</div>}
    </div>
  )
}
